/**
 * @file useOpenSite.ts
 * @description «Открыть сайт» из кабинета магазина с учётом паузы (phase19.16,
 * SB138; решение оркестратора к §7.25): «⋯ → Открыть сайт» конструктора и
 * кнопки «Открыть» в сообщениях после публикации.
 *  - сайт закрыт магазином — витрина открывается через пропуск сотрудника
 *    (`useSiteAccessActions().watch`: вкладка синхронно по нажатию, адрес
 *    пропуска — после ответа), иначе сотрудник увидел бы заглушку;
 *  - сайт закрыт платформой — пропуск не выдаётся (409), открываем как
 *    раньше и подсказываем, что на сайте заглушка;
 *  - сайт открыт или состояние неизвестно — как раньше, адрес витрины.
 * Состояние читается в момент нажатия (`siteAccessEntry`), а не при
 * отрисовке: кнопка в сообщении могла появиться до закрытия сайта.
 */

import { useNotify } from "../../components/ui/NotifyProvider";
import { siteAccessEntry, siteAccessNow } from "../../lib/site-access/site-access-store";
import { activeSiteClosure } from "../../lib/site-access/site-access-view";
import { liveFrameUrl } from "../../lib/site-view/preview";
import { useShopContextKey, useSiteAccess } from "./useSiteAccess";
import { useSiteAccessActions } from "./useSiteAccessActions";

/** Подсказка при паузе платформы. */
export const SITE_CLOSED_BY_PLATFORM_HINT = "Сайт закрыт платформой Commora — вы увидите заглушку, как покупатели.";

/** Функция «открыть сайт на странице `path`»; `null` — у магазина нет адреса сайта. */
export function useOpenSite(siteUrl: string | null): ((path?: string) => void) | null {
  const key = useShopContextKey();
  const access = useSiteAccess(key, "read");
  const actions = useSiteAccessActions(access);
  const notify = useNotify();
  if (!siteUrl) return null;

  return (path = "/") => {
    const entry = siteAccessEntry();
    const own = key && entry?.key === key ? entry : null;
    const closure = activeSiteClosure(own?.data ?? null, siteAccessNow(own));
    // Магазин закрыт сам или ещё не открыт (PL8) — смотреть через пропуск сотрудника.
    if (closure?.source === "shop" || closure?.source === "launch") {
      actions.watch(path);
      return;
    }
    if (closure?.source === "platform") notify.info(SITE_CLOSED_BY_PLATFORM_HINT);
    window.open(liveFrameUrl(siteUrl, path), "_blank", "noopener");
  };
}
